import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTheme } from '@emotion/react';

// utils
import { dataIsValid } from 'shared/utils';
import formatPhoneNumber from 'shared/utils/formatting/formatPhoneNumber';

// Our Constants
import {
	ADVISOR_MANUAL,
	ADVISOR_INTEGRATION,
	ADVISOR_BATCH,
	REFERRAL_PARTNER,
	SELF,
	UNKNOWN
} from 'shared/constants/OnboardingSource/OnboardingSourceTypes';
import PropTypes from 'prop-types';

/**
 * This flag means the company has turned on source data
 */
const ONBOARDING_SOURCE_META_COMPANY_LIST =
	process.env.REACT_APP_ONBOARDING_SOURCE_META_COMPANY_LIST;

const ONBOARDING_SOURCE_TYPE = {
	[ADVISOR_MANUAL]: (advisorName) => `${advisorName}`,
	[ADVISOR_INTEGRATION]: (advisorName) => `${advisorName}`,
	[ADVISOR_BATCH]: (advisorName) => `${advisorName}`,
	[REFERRAL_PARTNER]: (referralPartner) => {
		const { firstname, lastname } = referralPartner;
		return `${firstname} ${lastname}`;
	},
	[SELF]: 'Self Onboard',
	UNKNOWN: 'Unknown'
};

const getOnboardingSourceOutput = (
	onboardingSourceType,
	referralPartner,
	advisorName
) => {
	if (onboardingSourceType === SELF)
		return ONBOARDING_SOURCE_TYPE[onboardingSourceType];

	if (onboardingSourceType === REFERRAL_PARTNER)
		return ONBOARDING_SOURCE_TYPE[onboardingSourceType](referralPartner);

	return ONBOARDING_SOURCE_TYPE[onboardingSourceType](advisorName);
};

function SoraWalletOnboardingSource({
	advisor,
	advisorName,
	client,
	onboardingSourceType,
	referralPartner
}) {
	const SoraTheme = useTheme();
	const { primary } = SoraTheme.palette;

	const isMetaCompany = useMemo(() => {
		if (!ONBOARDING_SOURCE_META_COMPANY_LIST) return false;
		try {
			const parsedMetaCompanyList = JSON.parse(
				ONBOARDING_SOURCE_META_COMPANY_LIST
			);

			return parsedMetaCompanyList.includes(advisor.companyId);
		} catch (e) {
			return false;
		}
	}, [client]);

	const onboardingSource = useMemo(() => {
		if (
			!dataIsValid(onboardingSourceType) ||
			onboardingSourceType === UNKNOWN
		)
			return null;

		return getOnboardingSourceOutput(
			onboardingSourceType,
			referralPartner,
			advisorName
		);
	}, [onboardingSourceType, referralPartner, advisorName]);

	const clientFirstName = useMemo(() => {
		if (!dataIsValid(client?.firstName)) return 'Unknown';
		return client.firstName;
	}, [client]);

	const clientLastName = useMemo(() => {
		if (!dataIsValid(client?.lastName)) return 'Unknown';
		return client.lastName;
	}, [client]);

	const clientPhone = useMemo(() => {
		if (!dataIsValid(client?.phone)) return 'Unknown';
		return formatPhoneNumber(client.phone);
	}, [client]);

	const isSoraEmail = useMemo(() => {
		if (!dataIsValid(client?.email)) return false;
		return client.email.includes('@sorafinance.com');
	}, [client]);

	const formattedEmail = useMemo(() => {
		if (!dataIsValid(client?.email)) return 'Unknown';
		if (isSoraEmail) return null;
		return client.email;
	}, [client]);

	if (!dataIsValid(onboardingSourceType) || onboardingSourceType === UNKNOWN)
		return <div style={{ display: 'none' }} />;

	return (
		<>
			<Typography
				variant="body2"
				sx={{
					margin: '8px 32px 0px 8px',
					color: primary.black
				}}
			>
				Onboarding Source: {onboardingSource}
			</Typography>
			{isMetaCompany && (
				<>
					<Typography
						variant="body2"
						sx={{
							margin: '8px 32px 0px 8px',
							color: primary.black
						}}
					>
						Client Name: {clientFirstName} {clientLastName}
					</Typography>
					<Typography
						variant="body2"
						sx={{
							margin: '8px 32px 0px 8px',
							color: primary.black
						}}
					>
						Phone: {clientPhone}{' '}
						{!isSoraEmail && `| Email: ${formattedEmail}`}
					</Typography>
				</>
			)}
		</>
	);
}

SoraWalletOnboardingSource.propTypes = {
	advisorName: PropTypes.string.isRequired,
	advisor: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		company: PropTypes.string,
		compannyId: PropTypes.number
	}).isRequired,
	client: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		clientId: PropTypes.number,
		phone: PropTypes.string,
		email: PropTypes.string
	}).isRequired,
	onboardingSourceType: PropTypes.string.isRequired,
	referralPartner: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string
	}).isRequired
};

export default SoraWalletOnboardingSource;
